// Community trust & safety: reports, blocks, hides, suspensions.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const REASONS = ["spam", "harassment", "hate", "sexual", "violence", "self_harm", "misinfo", "other"] as const;
const KINDS = ["song_comment", "content_comment", "circle_post", "wisdom_post", "profile"] as const;
type Kind = (typeof KINDS)[number];

const KIND_TO_TABLE: Record<Exclude<Kind, "profile">, string> = {
  song_comment: "song_comments",
  content_comment: "content_comments",
  circle_post: "circle_posts",
  wisdom_post: "wisdom_posts",
};

async function assertAdmin(context: { supabase: any; userId: string }) {
  const { data, error } = await context.supabase.rpc("has_role", {
    _user_id: context.userId,
    _role: "admin",
  });
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden");
}

async function getAuthorId(kind: Kind, id: string): Promise<string | null> {
  if (kind === "profile") return id;
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await (supabaseAdmin as any).from(KIND_TO_TABLE[kind]).select("user_id").eq("id", id).maybeSingle();
  return (data as any)?.user_id ?? null;
}


// -------- Report content --------
export const reportContent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { targetKind: Kind; targetId: string; reason: (typeof REASONS)[number]; notes?: string }) =>
    z
      .object({
        targetKind: z.enum(KINDS),
        targetId: z.string().uuid(),
        reason: z.enum(REASONS),
        notes: z.string().max(1000).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { checkRateLimit } = await import("./rate-limit.server");
    const ok = await checkRateLimit(`report:${context.userId}`, 5, 3600);
    if (!ok) throw new Error("Too many reports. Please try again later.");

    const authorId = await getAuthorId(data.targetKind, data.targetId);
    if (authorId && authorId === context.userId) throw new Error("You cannot report your own content.");

    const { error } = await context.supabase.from("content_reports").insert({
      reporter_id: context.userId,
      target_kind: data.targetKind,
      target_id: data.targetId,
      reason: data.reason,
      notes: data.notes ?? null,
    });
    if (error) {
      if ((error as any).code === "23505") return { ok: true, duplicate: true };
      throw new Error(error.message);
    }
    return { ok: true };
  });

// -------- Blocks --------
export const blockUser = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { userId: string }) => z.object({ userId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    if (data.userId === context.userId) throw new Error("You cannot block yourself.");
    const { error } = await context.supabase
      .from("user_blocks")
      .insert({ blocker_id: context.userId, blocked_id: data.userId });
    if (error && (error as any).code !== "23505") throw new Error(error.message);
    return { ok: true };
  });

export const unblockUser = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { userId: string }) => z.object({ userId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("user_blocks")
      .delete()
      .eq("blocker_id", context.userId)
      .eq("blocked_id", data.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const listMyBlocks = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("user_blocks")
      .select("blocked_id, created_at")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

// -------- Admin queue --------
export const listReports = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { status?: string; kind?: string } | undefined) =>
    z
      .object({
        status: z.enum(["open", "reviewing", "actioned", "dismissed"]).optional(),
        kind: z.enum(KINDS).optional(),
      })
      .parse(input ?? {}),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let q = supabaseAdmin
      .from("content_reports")
      .select("id, reporter_id, target_kind, target_id, reason, notes, status, resolution, created_at, resolved_at")
      .order("created_at", { ascending: false })
      .limit(200);
    q = q.eq("status", data.status ?? "open");
    if (data.kind) q = q.eq("target_kind", data.kind);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

// Set hidden flag on a target
async function setHidden(kind: Kind, id: string, hidden: boolean, reason: string | null) {
  if (kind === "profile") return;
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const patch = hidden
    ? { hidden_at: new Date().toISOString(), hidden_reason: reason, is_hidden: true }
    : { hidden_at: null, hidden_reason: null, is_hidden: false };
  const { error } = await (supabaseAdmin as any).from(KIND_TO_TABLE[kind]).update(patch).eq("id", id);

  if (error) throw new Error(error.message);
}


export const resolveReport = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: {
    id: string;
    resolution: "hide" | "unhide" | "dismiss" | "warn" | "suspend";
    reason?: string;
    suspendDays?: number;
  }) =>
    z
      .object({
        id: z.string().uuid(),
        resolution: z.enum(["hide", "unhide", "dismiss", "warn", "suspend"]),
        reason: z.string().max(500).optional(),
        suspendDays: z.number().int().min(1).max(365).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: report, error: rErr } = await supabaseAdmin
      .from("content_reports")
      .select("id, target_kind, target_id")
      .eq("id", data.id)
      .maybeSingle();
    if (rErr) throw new Error(rErr.message);
    if (!report) throw new Error("Report not found");

    const kind = report.target_kind as Kind;
    const targetId = report.target_id as string;
    const authorId = await getAuthorId(kind, targetId);

    let action: string = data.resolution;
    let newStatus: "actioned" | "dismissed" = "actioned";

    if (data.resolution === "hide") {
      await setHidden(kind, targetId, true, data.reason ?? "moderator hide");
      action = "hide";
    } else if (data.resolution === "unhide") {
      await setHidden(kind, targetId, false, null);
      action = "unhide";
    } else if (data.resolution === "dismiss") {
      newStatus = "dismissed";
    } else if (data.resolution === "warn" && authorId) {
      await supabaseAdmin.rpc as any; // no-op placeholder
      await supabaseAdmin
        .from("profiles")
        .update({ strike_count: (undefined as any) })
        .eq("id", authorId); // strike increment done below via raw
      const { data: prof } = await supabaseAdmin.from("profiles").select("strike_count").eq("id", authorId).maybeSingle();
      const nextStrikes = ((prof as any)?.strike_count ?? 0) + 1;
      await supabaseAdmin.from("profiles").update({ strike_count: nextStrikes }).eq("id", authorId);
      action = "warn_user";
    } else if (data.resolution === "suspend" && authorId) {
      const until = new Date(Date.now() + (data.suspendDays ?? 7) * 86400 * 1000).toISOString();
      await supabaseAdmin.from("profiles").update({ suspended_until: until }).eq("id", authorId);
      await setHidden(kind, targetId, true, "suspension");
      action = "suspend_user";
    }

    await supabaseAdmin
      .from("content_reports")
      .update({
        status: newStatus,
        resolution: data.resolution,
        resolver_id: context.userId,
        resolved_at: new Date().toISOString(),
      })
      .eq("id", data.id);

    await supabaseAdmin.from("moderation_actions").insert({
      moderator_id: context.userId,
      target_kind: kind,
      target_id: targetId,
      action: action as any,

      reason: data.reason ?? null,
    });

    return { ok: true };
  });

export const listRecentActions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("moderation_actions")
      .select("id, moderator_id, target_kind, target_id, action, reason, created_at")
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) throw new Error(error.message);
    return data ?? [];
  });
